import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import { Plus, Car, User, Camera, Shield } from "lucide-react";
import ANPRSimulation from "../components/anpr-simulation";
import AccessControlPanel from "../components/access-control-panel";

export default function Visits() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showAccessControl, setShowAccessControl] = useState(false);
  const [formData, setFormData] = useState({
    siteId: 1,
    subjectType: "vehicle",
    subjectExternalId: "",
    plateNumber: "",
    driverName: ""
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: visits, isLoading } = useQuery({
    queryKey: ["/api/visits"],
    queryFn: api.getVisits,
    refetchInterval: 10000, // Refresh every 10 seconds to pick up OCR updates
  });

  const createVisitMutation = useMutation({
    mutationFn: api.createVisit,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/visits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/active-visits"] });
      setIsDialogOpen(false);
      setShowAccessControl(false);
      setFormData({
        siteId: 1,
        subjectType: "vehicle", 
        subjectExternalId: "",
        plateNumber: "",
        driverName: ""
      });
      toast({
        title: "Visit Created",
        description: `Access ${data.status} - Vehicle directed to ${data.next}`,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create visit",
        variant: "destructive",
      });
    }
  });

  const handlePlateDetected = (plateNumber: string) => {
    setFormData(prev => ({ 
      ...prev, 
      plateNumber,
      subjectExternalId: plateNumber // Auto-fill external ID with plate
    }));
    setShowAccessControl(true);
  };

  const handleFaceDetected = (confidence: number) => {
    // Could trigger additional security checks based on confidence
    if (confidence < 70) {
      toast({
        title: "Low Face Recognition Confidence",
        description: "Manual verification may be required",
        variant: "destructive",
      });
    }
  };

  const handleAccessDecision = (decision: "PASS" | "HOLD" | "REJECT", reason?: string) => {
    const visitData = {
      ...formData,
      accessDecision: decision,
      reasons: reason
    };
    createVisitMutation.mutate(visitData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createVisitMutation.mutate(formData);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-blue-100 text-blue-800";
    }
  };

  return (
    <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Visits & Access Control</h1>
              <p className="text-muted-foreground">Manage visitor entry and access decisions</p>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="button-create-visit">
                  <Plus className="w-4 h-4 mr-2" />
                  New Visit
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <Camera className="w-5 h-5 mr-2" />
                    Vehicle Entry & Access Control
                  </DialogTitle>
                </DialogHeader>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* ANPR Simulation */}
                  <div>
                    <ANPRSimulation 
                      onPlateDetected={handlePlateDetected}
                      onFaceDetected={handleFaceDetected}
                    />
                  </div>

                  {/* Manual Entry Form */}
                  <div>
                    <Card>
                      <CardHeader>
                        <CardTitle>Manual Entry</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div>
                            <Label htmlFor="subjectType">Subject Type</Label>
                            <Select 
                              value={formData.subjectType} 
                              onValueChange={(value) => setFormData(prev => ({ ...prev, subjectType: value }))}
                            >
                              <SelectTrigger data-testid="select-subject-type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="vehicle">Vehicle</SelectItem>
                                <SelectItem value="driver">Driver</SelectItem>
                                <SelectItem value="visitor">Visitor</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label htmlFor="plateNumber">Plate Number</Label>
                            <Input
                              id="plateNumber"
                              value={formData.plateNumber}
                              onChange={(e) => setFormData(prev => ({ ...prev, plateNumber: e.target.value }))}
                              placeholder="e.g., ABC-123GP"
                              data-testid="input-plate-number"
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="driverName">Driver Name</Label>
                            <Input
                              id="driverName"
                              value={formData.driverName}
                              onChange={(e) => setFormData(prev => ({ ...prev, driverName: e.target.value }))}
                              placeholder="Enter driver name"
                              data-testid="input-driver-name"
                            />
                          </div>

                          <div>
                            <Label htmlFor="subjectExternalId">External ID</Label>
                            <Input
                              id="subjectExternalId"
                              value={formData.subjectExternalId}
                              onChange={(e) => setFormData(prev => ({ ...prev, subjectExternalId: e.target.value }))}
                              placeholder="Enter external ID"
                              required
                              data-testid="input-external-id"
                            />
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Access Control Panel */}
                {showAccessControl && formData.plateNumber && (
                  <div className="mt-6">
                    <AccessControlPanel 
                      plateNumber={formData.plateNumber}
                      driverName={formData.driverName}
                      onDecision={handleAccessDecision}
                    />
                  </div>
                )}

                <div className="flex justify-end space-x-2 mt-6">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsDialogOpen(false);
                    setShowAccessControl(false);
                  }}>
                    Cancel
                  </Button>
                  {!showAccessControl && (
                    <Button 
                      onClick={() => setShowAccessControl(true)}
                      disabled={!formData.plateNumber || !formData.subjectExternalId}
                      data-testid="button-proceed-access-control"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Proceed to Access Control
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Visits</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : visits && visits.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Subject</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Plate</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Driver</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Access</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">OCR Requested</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Started</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {visits.map((visit) => (
                        <tr key={visit.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => window.location.href = `/visits/${visit.id}`}>
                          <td className="px-4 py-4 text-sm font-medium" data-testid={`visit-id-${visit.id}`}>
                            #{visit.id}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              {visit.subjectType === "vehicle" ? (
                                <Car className="w-4 h-4 mr-2 text-blue-500" />
                              ) : (
                                <User className="w-4 h-4 mr-2 text-green-500" />
                              )}
                              <div>
                                <div className="text-sm font-medium">{visit.subjectExternalId}</div>
                                <div className="text-xs text-muted-foreground">{visit.subjectType}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm">{visit.plateNumber || "-"}</td>
                          <td className="px-4 py-4 text-sm">{visit.driverName || "-"}</td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(visit.status)}`}>
                              {visit.status}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              visit.accessDecision === "PASS" 
                                ? "bg-green-100 text-green-800" 
                                : visit.accessDecision === "HOLD"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}>
                              {visit.accessDecision || "Pending"}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-1">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                visit.ocrRequested 
                                  ? "bg-blue-100 text-blue-800" 
                                  : "bg-gray-100 text-gray-600"
                              }`}>
                                {visit.ocrRequested ? "Yes" : "No"}
                              </span>
                              {visit.ocrRequestedAt && (
                                <span className="text-xs text-muted-foreground">
                                  {new Date(visit.ocrRequestedAt).toLocaleString()}
                                </span>
                              )}
                              {visit.ocrProcessed && (
                                <span className="text-xs text-green-600 font-medium">
                                  Processed
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-muted-foreground">
                            {new Date(visit.startedAt).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No visits found. Create your first visit to get started.
                </div>
              )}
            </CardContent>
          </Card>
    </div>
  );
}
